import { useApplicationStatus } from "../hooks/useApplicationStatus";
import type { ApplicationStatusState, PortalApplication } from "../types/portal";
import { Icon } from "./Icon";
import { useAuth } from "../auth/AuthContext";

const statusLabels: Record<ApplicationStatusState, string> = {
  checking: "Checking…",
  online: "Online",
  offline: "Offline",
  degraded: "Offline",
  authentication_required: "Online – Login Required",
  unknown: "Status Unknown",
};

function formatUrl(url: string) {
  return url.replace(/^https?:\/\//, "").replace(/\/$/, "");
}

function formatCheckedAt(value: string) {
  if (!value) return "Not checked yet";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

export function ApplicationCard({ application }: { application: PortalApplication }) {
  const { profile, loading } = useAuth();
  const status = useApplicationStatus(application.statusApiEndpoint, application.enabled && application.statusCheckEnabled);
  const isComingSoon = application.comingSoon || !application.enabled;
  const hasAccess = Boolean(profile && application.accessCode && profile.effectiveApplications.includes(application.accessCode));
  const guardedHref = !application.publicUrl ? "#" : application.publicAccess
    ? application.publicUrl
    : !profile
    ? `/login?returnTo=${encodeURIComponent(application.publicUrl)}`
    : hasAccess ? application.publicUrl : `/profile?application=${encodeURIComponent(application.id)}`;
  const opensExternally = hasAccess && !application.publicAccess;

  return (
    <article className={`application-card ${isComingSoon ? "application-card--coming" : "application-card--active"}`}>
      <div className="application-card__heading">
        <span className="application-icon"><Icon name={application.icon} /></span>
        <div>
          <h3>{application.name}</h3>
          <span className="category-badge">{application.category}</span>
        </div>
      </div>

      <p className="application-card__description">{application.description}</p>

      {isComingSoon ? (
        <div className="coming-soon">
          <span className="coming-soon__badge"><Icon name="clock" />Coming Soon</span>
          <span>Planned for upcoming release</span>
        </div>
      ) : (
        <>
          {application.showUrl === false ? (
            <span className="application-url application-url--placeholder" aria-hidden="true">
              <span>◎</span>{formatUrl(application.publicUrl ?? "")}
            </span>
          ) : (
            <a className="application-url" href={guardedHref} target={opensExternally ? "_blank" : undefined} rel={opensExternally ? "noopener noreferrer" : undefined}>
              <span aria-hidden="true">◎</span>{formatUrl(application.publicUrl ?? "")}
            </a>
          )}
          <div className="application-status" aria-live="polite" title={status.error ?? undefined}>
            <span className={`status-badge status-badge--${status.state}`}>
              <span className="status-dot" aria-hidden="true" />
              <span>{statusLabels[status.state]}</span>
            </span>
            <div className="status-meta">
              <span>Last checked: {formatCheckedAt(status.checkedAt)}</span>
              <button
                className="refresh-button"
                type="button"
                onClick={() => void status.refresh()}
                disabled={status.isLoading}
                aria-label={`Refresh ${application.name} status`}
              >
                <Icon name="refresh" className={status.isLoading ? "icon--spinning" : ""} />
              </button>
            </div>
          </div>
          <a className="button button--card" href={guardedHref} target={opensExternally ? "_blank" : undefined} rel={opensExternally ? "noopener noreferrer" : undefined} aria-disabled={loading && !application.publicAccess}>
            {loading && !application.publicAccess ? "Checking access…" : application.publicAccess ? "Open Application" : !profile ? "Log in to open" : hasAccess ? "Open Application" : "View access status"}<Icon name={opensExternally ? "external" : "arrow"} />
          </a>
        </>
      )}
    </article>
  );
}
